import { useCallback, useState } from 'react';
import {
  GridToolbarContainer,
  GridRowEditStopReasons,
  GridTreeDataGroupingCell,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { FieldArray, useFormikContext } from 'formik';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { dataGridPremiumColumns } from '../../../utils/helper';

import {
  DataGridIdentifiers,
  AddButtonUi,
  DatagridPremiumUi,
  ButtonUI,
  AutoCompleteSelectUI,
  FormLabelUI,
} from '../../Interface';
import TaskListDialog from '../TaskListDialog';
import { DependenceOption } from '../../../config/taskList';
import { Box, Typography } from '@mui/material';
import TaskListAttachmentDialog from '../TaskListAttachmentDialog';
import useParam from '../../../context/ParamContext';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import ProjectTaskDialog from '../../ProjectTasks/ProjectTaskDialog';

function EditToolbar(props) {
  const { disabled, setIsDialogOpen, setRows, isExtraWorkType } = props;
  const { values } = useFormikContext();

  const handleClick = () => {
    setRows(values.taskList);
    setIsDialogOpen(true);
  };

  return (
    <GridToolbarContainer>
      {!isExtraWorkType && <AddButtonUi disabled={disabled} onClick={handleClick} />}
    </GridToolbarContainer>
  );
}

const ProjectTaskList = ({
  disabled,
  defaultTaskList,
  autocompleteData,
  autoCompleteOptions,
  setRefetchTable,
  user,
  projectStartDate,
  isExtraWorkType,
  setSelectedValues,
}) => {
  const apiRef = useGridApiRef();
  const { params } = useParam();
  const { values, setFieldValue } = useFormikContext();
  const [rows, setRows] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAttachDialogOpen, setIsAttachDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});

  const [rowModesModel, setRowModesModel] = useState({});

  const defTaskDialogData = {
    open: false,
    id: null,
    index: null,
  };
  const [taskDialogData, setTaskDialogData] = useState(defTaskDialogData);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const ResponsibleFilterInput = ({ item, applyValue, label, ...other }) => {
    const options = autoCompleteOptions?.responsible;

    const selectedOption =
      options?.find((opt) => opt.value === item.value?.value && opt.type === item.value?.type) ?? null;

    return (
      <AutoCompleteSelectUI
        label={<FormLabelUI text={label} />}
        variant="standard"
        dataset={options}
        getOptionLabel={(o) => o.label}
        value={selectedOption}
        onChange={(_, value) =>
          applyValue({
            ...item,
            value,
          })
        }
      />
    );
  };

  const columns = () => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        renderCell: (params) => {
          return <GridTreeDataGroupingCell {...params} label="" />;
        },
      },
      {
        field: 'startDate',
        headerName: 'Kezdő dátum',
        type: 'date',
        valueGetter: (_data, row) => {
          return row.startDate ? new Date(row.startDate) : '';
        },
      },
      {
        field: 'duration',
        headerName: 'Időintervallum (nap)',
        type: 'string',
      },
      {
        field: 'waitingTime',
        headerName: 'Várakozási idő (nap)',
        type: 'string',
      },
      {
        field: 'statusId',
        headerName: 'Státusz',
        type: 'string',
        valueGetter: (_, data) => {
          const findedStatus = autoCompleteOptions?.taskListStatus?.find((item) => item.value === data?.statusId);
          return findedStatus?.label;
        },
      },
      {
        field: 'responsible',
        headerName: 'Felelős',
        type: 'string',
        valueGetter: (_, data) => {
          const findedRespPerson = autoCompleteOptions?.responsible?.find(
            (item) =>
              (data.responsiblePersonId ??
                data.responsibleUserGroupId ??
                data.responsibleCompanyId ??
                data.responsibleWorkerId) === item.value && item.type === data?.responsibleType
          );
          return findedRespPerson?.label;
        },
        renderCell: (params) => {
          const data = params.row;
          const relevantValue =
            data.responsiblePersonId ??
            data.responsibleUserGroupId ??
            data.responsibleCompanyId ??
            data.responsibleWorkerId;

          const option = autoCompleteOptions?.responsible?.find(
            (item) => item.value === relevantValue && item.type === data?.responsibleType
          );

          return (
            <div>
              {option?.icon ? <span style={{ display: 'inline-flex', marginRight: 8 }}>{option.icon}</span> : null}
              {option?.label}
            </div>
          );
        },
        filterOperators: [
          {
            label: 'Egyenlő',
            value: 'is',
            InputComponent: ResponsibleFilterInput,
            getApplyFilterFn: (filterItem, column) => {
              if (!filterItem.field || !filterItem.value || !filterItem.operator) {
                return null;
              }

              return (value, row, column, apiRef) => {
                return (
                  row.responsibleType === filterItem.value.type &&
                  (row?.responsiblePersonId ??
                    row?.responsibleUserGroupId ??
                    row?.responsibleCompanyId ??
                    row?.responsibleWorkerId) === filterItem.value.value
                );
              };
            },
          },
          {
            label: 'Nem egyenlő',
            value: 'not',
            InputComponent: ResponsibleFilterInput,
            getApplyFilterFn: (filterItem, column) => {
              if (!filterItem.field || !filterItem.value || !filterItem.operator) {
                return null;
              }

              return (value, row, column, apiRef) => {
                return !(
                  row.responsibleType === filterItem.value.type &&
                  (row?.responsiblePersonId ??
                    row?.responsibleUserGroupId ??
                    row?.responsibleCompanyId ??
                    row?.responsibleWorkerId) === filterItem.value.value
                );
              };
            },
          },
        ],
        headerFilterOperators: [
          {
            label: 'Egyenlő',
            value: 'is',
            InputComponent: ResponsibleFilterInput,
            getApplyFilterFn: (filterItem, column) => {
              if (!filterItem.field || !filterItem.value || !filterItem.operator) {
                return null;
              }

              return (value, row, column, apiRef) => {
                return (
                  row.responsibleType === filterItem.value.type &&
                  (row?.responsiblePersonId ??
                    row?.responsibleUserGroupId ??
                    row?.responsibleCompanyId ??
                    row?.responsibleWorkerId) === filterItem.value.value
                );
              };
            },
          },
          {
            label: 'Nem egyenlő',
            value: 'not',
            InputComponent: ResponsibleFilterInput,
            getApplyFilterFn: (filterItem, column) => {
              if (!filterItem.field || !filterItem.value || !filterItem.operator) {
                return null;
              }

              return (value, row, column, apiRef) => {
                return !(
                  row.responsibleType === filterItem.value.type &&
                  (row?.responsiblePersonId ??
                    row?.responsibleUserGroupId ??
                    row?.responsibleCompanyId ??
                    row?.responsibleWorkerId) === filterItem.value.value
                );
              };
            },
          },
        ],
      },
      {
        field: 'userGroupId',
        headerName: 'Felelősök (csoport)',
        type: 'string',
        valueGetter: (_, data) => {
          const findedRespPerson = autoCompleteOptions?.userGroups?.find((item) => item.value === data?.userGroupId);
          return findedRespPerson?.label;
        },
      },
      {
        field: 'userId',
        headerName: 'Tulajdonos',
        type: 'string',
        valueGetter: (_, data) => {
          const findedUser = autoCompleteOptions?.user?.find((item) => item.value === data?.userId);
          return findedUser?.label;
        },
      },
      {
        field: 'dependence',
        headerName: 'Függőség',
        type: 'string',
        filterable: false,
        renderCell: (data) => {
          const foundedDep = DependenceOption?.find((item) => item.value === data.row?.dependence);
          return foundedDep?.label;
        },
      },
      {
        field: 'typeId',
        headerName: 'Típus',
        type: 'string',
        valueGetter: (_, data) => {
          const findedType = autoCompleteOptions?.taskListType?.find((item) => item.value === data?.typeId);
          return findedType?.label;
        },
      },
      {
        field: 'preparedness',
        headerName: 'Készültség',
        valueGetter: (_, params) => {
          return params?.preparedness ? `${params?.preparedness ?? 0} %` : '';
        },
      },
      {
        field: 'deadLine',
        headerName: 'Határidő',
        type: 'dateTime',
        valueGetter: (_data, row) => {
          return row.deadLine ? new Date(row.deadLine) : null;
        },
      },
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
      },
      {
        field: 'taskListAttachment',
        headerName: 'Csatolmányok',
        type: 'string',
        filterable: false,
        renderCell: (data) => {
          return data.row.isNew || data.row.isVirtual ? (
            ''
          ) : (
            <ButtonUI
              size="sm"
              className="bg-success px-2.5 py-1"
              onClick={() => {
                setIsAttachDialogOpen(true);
                setSelectedRow(data.row);
              }}
              text={
                <Box display="flex">
                  <AttachFileIcon fontSize="small" />
                  <Typography fontSize="medium">{data?.row?.taskListAttachment?.length}</Typography>
                </Box>
              }
            />
          );
        },
      },
      {
        field: 'construction',
        type: 'boolean',
        sortable: true,
        headerName: 'Kivitelezés',
      },
      {
        field: 'timeSpentTotal',
        headerName: 'Ráfordítás',
        type: 'number',
        valueGetter: (_, row) => {
          return row?.taskListReport?.reduce((sum, r) => sum + (parseFloat(r.timeSpent) || 0), 0);
        },
      },

      {
        field: 'visOrder',
        type: 'string',
        sortable: false,
        headerName: 'Sorrend',
        filterable: false,
      },

      {
        field: 'moreRespPerson',
        type: 'string',
        sortable: true,
        headerName: 'További felelősök',
        renderCell: (data) => {
          const ids = data.row?.taskListRespPerson?.map((respPerson) => respPerson.userId);

          return autoCompleteOptions?.user
            ?.filter((u) => ids?.includes(u.value))
            ?.map((u) => u.label)
            ?.join(', ');
        },
      },
      ...(params.TASKLIST_SHOW_TASKRATIO_FIELD === 'Y'
        ? [
            {
              field: 'taskRatio',
              headerName: 'Feladat arány',
              type: 'number',
              sortable: false,
            },
          ]
        : []),
      ...(params.TASKLIST_SHOW_BRIGADE_MANAGEMENT === 'Y'
        ? [
            {
              field: 'brigadeId',
              headerName: 'Brigád',
              type: 'string',
              valueGetter: (_, data) => {
                const findedBrigade = autoCompleteOptions?.brigade?.find((item) => item.value === data?.brigadeId);
                return findedBrigade?.label;
              },
            },
            {
              field: 'taskListResourceOne',
              type: 'string',
              headerName: 'Dolgozók',
              valueGetter: (_, data) => {
                const resources = data.taskListResourceOne
                  ?.map((item) => item.label)
                  .filter(Boolean)
                  .join(', ');

                return resources || '';
              },
            },
          ]
        : []),
    ]);
  };

  const getTreeDataPath = useCallback((row) => row.path, []);

  const handleTaskDialogClose = () => {
    setTaskDialogData(defTaskDialogData);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="taskList">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridInitialState={{ density: 'compact' }}
                datagridIdentifier={DataGridIdentifiers.projectTaskList}
                height={600}
                columns={columns()}
                rows={
                  isExtraWorkType
                    ? values?.taskList
                        ?.filter((item) => item.typeId === parseInt(params.TASKLIST_EXTRA_WORK_TYPEID))
                        ?.map(({ id, ...rest }) => ({
                          ...rest,
                          id,
                          parentId: null,
                          path: [id],
                        })) ?? []
                    : values?.taskList ?? []
                }
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                treeData
                getTreeDataPath={getTreeDataPath}
                slots={{
                  toolbar: EditToolbar,
                }}
                groupingColDef={{
                  headerName: '',
                  renderCell: () => null,
                  width: 1,
                  hideable: false,
                  sortable: false,
                  resizable: false,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    setIsDialogOpen,
                    setRows,
                    isExtraWorkType,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;

                  if (disabled) return;

                  if (!Number.isInteger(_params.id)) {
                    addNotification({
                      content: 'A kiválasztott feladatot nem lehet megnyitni!',
                      type: NotificationType.WARNING,
                    });
                    return;
                  }

                  const index = values.taskList.findIndex((rc) => rc.id === _params.id);
                  setTaskDialogData({
                    open: true,
                    id: _params.id,
                    index,
                    ..._params.row,
                  });
                }}
                localeText={{
                  headerFilterOperatorEquals: 'Egyenlő',
                  headerFilterOperatorNotEquals: 'Nem egyenlő',
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
      <TaskListDialog
        open={isDialogOpen}
        handleClose={() => setIsDialogOpen(false)}
        autoCompleteOptions={autoCompleteOptions}
        autocompleteData={autocompleteData}
        rows={rows}
        defaultField={defaultTaskList}
        fromProject={true}
        setRefetchTable={setRefetchTable}
        projectStartDate={projectStartDate}
      />
      <TaskListAttachmentDialog
        open={isAttachDialogOpen}
        handleClose={() => setIsAttachDialogOpen(false)}
        row={{ selectedRow, setSelectedRow }}
        editable={false}
        apiRef={apiRef}
        setRefetchTable={setRefetchTable}
        user={user}
      />

      <ProjectTaskDialog
        open={taskDialogData.open}
        handleClose={handleTaskDialogClose}
        data={taskDialogData}
        setFieldValue={setFieldValue}
        setSelectedValues={setSelectedValues}
      />
    </div>
  );
};

export default ProjectTaskList;
